import { useEffect, useState } from 'react'
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { fonts } from '../../lib/theme'
import { RANK_COLORS, CATEGORY_COLORS, CATEGORY_ABBR } from '../../lib/rankColors'
import MemberPicker from './MemberPicker'
import { useThemeColors } from '../../lib/useThemeColors'
import { Card, SectionLabel } from './ui'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { Skeleton } from '../Home/ui'
const PODIO_BG = { 1: 'rgba(250,204,21,0.06)', 2: 'rgba(192,199,208,0.06)', 3: 'rgba(208,138,78,0.06)' }

function CatBadge({ cat }) {
  const tc = useThemeColors()
  const base = CATEGORY_COLORS[cat]
  const c = base ? { bg: base.bg ?? tc.rosa, text: base.text } : { bg: tc.bgCard3, text: tc.textMuted }
  return (
    <View style={[styles.catBadge, { backgroundColor: c.bg }]}>
      <Text style={[styles.catBadgeText, { color: c.text }]}>{CATEGORY_ABBR[cat] ?? cat}</Text>
    </View>
  )
}

// userId: se passato, l'Albo mostra quel giocatore e il selettore sparisce (usato nella Bacheca, che ha il suo selettore)
export default function AlboDoro({ stagionale, leagueId, currentUserId, userId }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const [pickedUserId, setSelectedUserId] = useState(currentUserId)
  const selectedUserId = userId ?? pickedUserId
  const [podi, setPodi] = useState([])
  const [loadingPodi, setLoadingPodi] = useState(false)

  const MOTIVAZIONI = [
    t('leagues.badge_empty_1'),
    t('leagues.badge_empty_2'),
    t('leagues.badge_empty_3'),
    t('leagues.badge_empty_4'),
  ]
  const isMe = selectedUserId === currentUserId
  const player = stagionale.find(s => s.out_id_user === selectedUserId)
  const playerIndex = stagionale.findIndex(s => s.out_id_user === selectedUserId)
  const motivazione = MOTIVAZIONI[playerIndex % MOTIVAZIONI.length]

  useEffect(() => {
    if (!selectedUserId || !leagueId) return
    setLoadingPodi(true)
    supabase
      .rpc('fn_albo_doro', { p_id_league: leagueId, p_id_user: selectedUserId })
      .then(({ data }) => { setPodi(data ?? []); setLoadingPodi(false) })
  }, [selectedUserId, leagueId])

  const vittorie = podi.filter(p => p.out_posizione === 1)
  const secondi = podi.filter(p => p.out_posizione === 2)
  const terzi = podi.filter(p => p.out_posizione === 3)

  return (
    <Card>
      {/* Header */}
      <View style={styles.headerRow}>
        <SectionLabel>{t('leagues.albo')}</SectionLabel>
        {userId == null && (
          <MemberPicker
            members={stagionale}
            value={selectedUserId}
            onChange={setSelectedUserId}
            placeholder="—"
            title={t('leagues.albo')}
            style={{ maxWidth: 190 }}
          />
        )}
      </View>

      {loadingPodi ? (
        <View style={{ gap: 10, paddingVertical: 8 }} accessible accessibilityLabel={t('common.loading')} accessibilityState={{ busy: true }}>
          <Skeleton height={44} radius={10} />
          <Skeleton height={44} radius={10} />
          <Skeleton height={44} radius={10} />
        </View>
      ) : podi.length === 0 ? (
        <View style={[styles.noPodioWrap, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
          <MaterialCommunityIcons name="trophy-outline" size={36} color={tc.textMuted} style={{ marginBottom: 10 }} />
          <Text style={[styles.noPodioText, { color: tc.textMuted }]}>
            {isMe ? motivazione : `${player?.out_display_name ?? t('leagues.player_a')} ${t('leagues.no_podio')}`}
          </Text>
        </View>
      ) : (
        <>
          {/* Contatori */}
          <View style={styles.podioCounts}>
            {[
              { label: t('leagues.victories'), list: vittorie, pos: 1 },
              { label: t('leagues.second'), list: secondi, pos: 2 },
              { label: t('leagues.third'), list: terzi, pos: 3 },
            ].map(({ label, list, pos }) => { const color = RANK_COLORS[pos]; return (
              <View
                key={label}
                style={[styles.podioCountItem, {
                  backgroundColor: tc.bgCard2,
                  borderColor: list.length > 0 ? color + '44' : tc.border,
                  opacity: list.length === 0 ? 0.4 : 1,
                }]}
              >
                <MaterialCommunityIcons name="medal" size={24} color={color} style={{ marginBottom: 4 }} />
                <Text style={[styles.podioCountNum, { color }]}>{list.length}</Text>
                <Text style={[styles.podioCountLabel, { color: tc.textMuted }]}>{label}</Text>
              </View>
            ) })}
          </View>

          {/* Lista podi */}
          {[{ list: vittorie, pos: 1 }, { list: secondi, pos: 2 }, { list: terzi, pos: 3 }]
            .filter(({ list }) => list.length > 0)
            .map(({ list, pos }) => {
              const color = RANK_COLORS[pos]
              return (
                <View key={pos} style={{ marginBottom: 10 }}>
                  <Text style={[styles.podioGroupLabel, { color }]}>
                    {pos === 1 ? t('leagues.victories') : pos === 2 ? t('leagues.second') : t('leagues.third')}
                  </Text>
                  {list.map(g => (
                    <View
                      key={g.out_id_race}
                      style={[styles.podioRaceRow, { backgroundColor: PODIO_BG[pos], borderColor: color + '44' }]}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 }}>
                        <MaterialCommunityIcons name="medal" size={20} color={color} />
                        <Text style={[styles.podioRaceName, { color: tc.text }]} numberOfLines={1}>
                          {g.out_race_name}
                        </Text>
                      </View>
                      {g.out_category && <CatBadge cat={g.out_category} />}
                    </View>
                  ))}
                </View>
              )
            })}
        </>
      )}
    </Card>
  )
}

const styles = StyleSheet.create({
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  noPodioWrap: { borderWidth: 1, borderStyle: 'dashed', borderRadius: 12, padding: 24, alignItems: 'center' },
  noPodioEmoji: { fontSize: 36, marginBottom: 10 },
  noPodioText: { fontSize: 13, lineHeight: 20, textAlign: 'center', fontStyle: 'italic' },
  podioCounts: { flexDirection: 'row', gap: 8, marginBottom: 16 },
  podioCountItem: { flex: 1, alignItems: 'center', borderRadius: 10, padding: 10, paddingVertical: 10, borderWidth: 1 },
  podioCountNum: { fontSize: 22, fontFamily: fonts.condensedBold, lineHeight: 24 },
  podioCountLabel: { fontSize: 10, marginTop: 2 },
  podioGroupLabel: { fontSize: 10, fontFamily: fonts.condensedBold, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 },
  podioRaceRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: 9, paddingHorizontal: 12, borderRadius: 10, borderWidth: 1, marginBottom: 5 },
  podioRaceName: { fontSize: 13, fontWeight: '700', flex: 1 },
  catBadge: { borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 },
  catBadgeText: { fontFamily: fonts.condensedBold, fontSize: 9, letterSpacing: 0.5, textTransform: 'uppercase' },
})